<template>
  <v-card>
    <v-card-title class="py-0">
      {{ isSubordinates ? "Subordinates Leave Applications" : "Nonsubordinates Leave Applications" }}
      <v-tooltip
        content-class="bottom"
        bottom
      >
        <template v-slot:activator="{ on }">
          <v-avatar 
            class="cursor-pointer"
            v-on="on"
            @click="loadLeaveApprovals"                
          >
            <v-icon medium>
              mdi-refresh
            </v-icon>
          </v-avatar>
        </template>
        <span>Reload</span>
      </v-tooltip>
      <v-spacer /><v-spacer />
      <v-autocomplete
        v-model="statusFilterId"
        :items="status"
        color="primary"
        item-text="name"
        item-value="id"
        placeholder="Filter by status"
        hide-no-data
        hide-selected
        :search-input.sync="search"
      />
    </v-card-title>
    <v-card-title class="py-0">
      <v-col
        cols="10"
        class="py-0"
      >
        <v-text-field
          v-model="searchContent"
          append-icon="mdi-magnify"
          label="Search by name"
          single-line
          hide-details
          clearable
          @input="leaveSearch"
        />
      </v-col>
      <v-col cols="2">
        <div
          class="mt-1"
          style="display: flex; justify-content: flex-end"
        >
          <v-switch
            v-model="isSubordinates"
            label="Subordinates"
          />
        </div>
      </v-col>
    </v-card-title>
    <v-data-table 
      item-key="tempid"
      :loading="loading"
      :headers="headers"
      :items="leaveApprovals"
      hide-default-footer
      class="leave-approval-listing"
      height="500px"
      :items-per-page="50"
    >
      <template v-slot:item.name="{ item }">
        <v-avatar
          color="teal"
          size="40"
          class="mx-2"
        >
          <img 
            v-if="item.employee.thumbnail_url"
            :src="item.employee.thumbnail_url" 
            alt="avatar"
          >
          <span 
            v-else 
            class="white--text headline"
          >{{ item.employee.person.name.match(/\b\w/g).slice(0, 1).join('') }}
          </span>
        </v-avatar>
        {{ (item.employee.person || {}).name }}
      </template>
      <template v-slot:item.type="{ item }">
        {{ item.leave_entitlement.leave_type.is_paid_leave ? 'Paid' : 'Unpaid' }}
      </template>
      <template v-slot:item.status="{ item }">
        <span
          :class="{ 
            'warning--text': (item.status.id === 100),
            'red--text': (item.status.id === 500), 
            'grey--text': (item.status.id === 501), 
            'green--text': (item.status.id === 200 || item.status.id === 201) 
          }"
        >
          {{ item.status.name }}
        </span>   
      </template>
      <template v-slot:item.action="{ item }">
        <employee-leaves-applications-approvals-dialog-edit
          :leave-application="item"
          :is-subordinates="isSubordinates==1 ? true: false"
          :disabled="item.status.id !== 100 && !$hasPermissionTo(['list all leave approvals'])"
          @updated="loadLeaveApprovals"
        />
      </template>
    </v-data-table>
    <v-container
      v-if="finishLoading"
      class="align-right"
    >
      <v-btn
        icon
        color="primary"
        :disabled="pageNumber === 1"
        @click="previousPage"
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <span
        class="pagination"
      >{{ from }}-{{ to }} of {{ total }}</span>
      <v-btn
        icon
        color="primary"
        :disabled="pageNumber === lastPage"
        @click="nextPage"
      >
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>
    </v-container>
  </v-card>
</template>

<script>
  export default {
    components: {
      EmployeeLeavesApplicationsApprovalsDialogEdit: () => import('@/components/employee/leaves/applications/approvals/DialogEdit'),
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        loading: false,
        leaveApprovals: [],
        status: [],
        statusFilterId: 100,
        search: null,
        headers: [
          {
            text: 'Employee',
            value: 'name',
          },
          {
            text: 'Leave Type',
            value: 'leave_entitlement.leave_type.name',
          },
          {
            text: 'Type',
            filterable: false,
            value: 'type',
          },
          {
            text: 'Start Date',
            value: 'start_date',
          },
          {
            text: 'End Date',
            value: 'end_date',
          },
          {
            text: 'Days',
            value: 'requested_days',
          },
          {
            text: 'Date of Request',
            filterable: false,
            value: 'created_at',
          },
          {
            text: 'Status',
            sortable: false,
            filterable: false,
            value: 'status',
          },
          {
            text: 'Actions',
            sortable: false,
            filterable: false,
            value: 'action',
          },
        ],        
        pageNumber: 0,
        total: 0,
        from: 0,
        to: 0,
        lastPage: 0,
        finishLoading: false,
        searchContent: null,
        isSubordinates: 1,
      }
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.loadApprovalStatus()
          }
        },
      },

      isSubordinates: {
        immediate: true,
        handler (value) {
          if (this.isSubordinates) { this.isSubordinates = 1 } else { this.isSubordinates = 0 }
          this.leaveSearch(this.searchContent)
        },
      },

      search (val) {
        let status = this.status.find(s => s.name === val)
        if (status) {
          this.loading = true
          this.$api.employeeLeaveApprovals().list(this.employeeUid, '?isSubordinates=' + this.isSubordinates, status.id)
            .then(response => {
              this.setPaginationDetails(response.data)
              this.leaveApprovals = this.getLeaveApplications(response.data.data) 
              this.loading = false
              this.finishLoading = true
            })
        }
      },
    },

    created () {
      this.loading = true
      this.$api.employeeLeaveApprovals().list(this.employeeUid, '?isSubordinates=' + this.isSubordinates, 100)
        .then(response => {
          this.setPaginationDetails(response.data)
          this.leaveApprovals = this.getLeaveApplications(response.data.data) 
          this.loading = false
          this.finishLoading = true
        })
    },

    methods: {
      leaveSearch (val) {
        this.loading = true
        clearTimeout(this._searchTimerId)
        this._searchTimerId = setTimeout(() => {
          if (val) {
            this.$api.employeeLeaveApprovals().search(this.statusFilterId, { name: this.searchContent, isSubordinates: this.isSubordinates })
              .then(response => {
                this.setPaginationDetails(response.data)
                this.leaveApprovals = this.getLeaveApplications(response.data.data) 
                this.loading = false
              })
          } else {
            this.loadLeaveApprovals()
          }
        }, 1000) /* 1000ms throttle */
      },

      nextPage () {
        this.loading = true
        this.$api.employeeLeaveApprovals().list(this.employeeUid, '?page=' + (this.pageNumber + 1) + '&isSubordinates=' + this.isSubordinates, this.statusFilterId)
          .then(response => {
            this.setPaginationDetails(response.data)
            this.leaveApprovals = this.getLeaveApplications(response.data.data) 
            this.loading = false
          })
      },

      previousPage () {
        this.loading = true
        this.$api.employeeLeaveApprovals().list(this.employeeUid, '?page=' + (this.pageNumber - 1) + '&isSubordinates=' + this.isSubordinates, this.statusFilterId)
          .then(response => {
            this.setPaginationDetails(response.data)
            this.leaveApprovals = this.getLeaveApplications(response.data.data) 
            this.loading = false
          })
      },

      loadLeaveApprovals () {
        this.loading = true
        
        this.$api.employeeLeaveApprovals().list(this.employeeUid, '?isSubordinates=' + this.isSubordinates, this.statusFilterId)
          .then(response => {
            this.setPaginationDetails(response.data)
            this.leaveApprovals = this.getLeaveApplications(response.data.data)
            this.loading = false
          })
      },
      loadApprovalStatus () {
        this.$api.approvalStatus().list()
          .then(response => {
            this.status = response.data.filter(item => {
              return item.id !== 502 && item.id !== 204
            })
            this.statusFilterId = 100
          })
      },
      getLeaveApplications (approvals) {  
        if (approvals) {
          return _.orderBy(approvals.map(approval => {
            const finalStatus = approval.approval_request.status ? approval.approval_request.status : approval.status

            return {
              ...approval.approval_request.approvable,
              approval: approval,
              employee: approval.approval_request.employee,
              tempid: approval.approval_request.employee.employee_uid + approval.id,
              status: finalStatus,
            }
          }), ['status.id', 'created_at'], ['asc', 'desc'])
        }
      },

      setPaginationDetails (page) {
        this.pageNumber = page.current_page
        this.total = page.total
        this.from = page.from
        this.to = page.to
        this.lastPage = page.last_page
      },
    },
  }
</script>

<style scoped>
.leave-approval-listing >>> ::-webkit-scrollbar {
  width: 3px !important; 
}

/* Track */
.leave-approval-listing >>> ::-webkit-scrollbar-track {
  box-shadow: inset 0 0 2px #e6e6e6; 
  border-radius: 10px;
}
 
/* Handle */
.leave-approval-listing >>> ::-webkit-scrollbar-thumb {
  background: grey; 
  border-radius: 10px;
  height: 2px;
}

/* Handle on hover */
.leave-approval-listing >>> ::-webkit-scrollbar-thumb:hover {
  background:  #555; 
}

.align-right{
  text-align: right;
}

.pagination{
  font-size:12px;
  color:#5F6368
}

.cursor-pointer{
  cursor: pointer;
}
</style>
